// ReactionsList.tsx
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Divider, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { useFetch } from "../../../hooks/useFetch2";
import { usePresetDialogs } from '../../dialog';
import { FormModal } from '../../modal/FormModal';
import ActionButtons, { ActionButtonProp } from '../../ActionButtons';
import ReactionForm from './ReactionForm';
import { ReactionType, ReactionToCustomer } from '../../../types/reaction';
import { useTranslation } from 'react-i18next';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3';
import { Assessment, Delete, Edit, MoreHoriz } from '@mui/icons-material';
import i18n from '../../../i18n';
import { api } from "../../../api/reactionApi2";
import { useAuthStore } from '../../../store/useAuthStore3';

export const ReactionsList: React.FC = () => {
    const { t } = useTranslation();
    const { data, setData, error } = useFetch<ReactionToCustomer[]>(api.getReactionToCustomer, []);
    const { data: reactionTypes, } = useFetch<ReactionType[]>(api.getReactionType, 
        [], 
        {lang: i18n.language }
    );
    const { customerId } = useAuthStore();
    const { showInfo, showConfirm, showPrompt, showOptions, DialogComponent } = usePresetDialogs();
    const [selectedReactionToCustomer, setSelectedReactionToCustomer] = useState<ReactionToCustomer | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (!customerId) {
        return <Typography>t('customer.loading')</Typography>;
    }    

    const columns: GridColDef[] = [
        {field: "id", headerName: 'id', type: "number", width: 70, },
        { field: "reaction_type_id", headerName: t("ReactionsList.Reaction_Type_Label"), type: "string", flex: 2, width: 100, display: 'flex',
            renderCell: (params: GridRenderCellParams<ReactionToCustomer>) => {
                return (
                    <Typography>
                        {reactionTypes.find(rt => rt.reaction_type_id === params.row.reaction_type_id)?.name}
                    </Typography>
                );
            }
        },
        { field: "description", headerName: t("ReactionsList.description_Label"), type: "string", flex: 2, width: 100, display: 'flex',
            renderCell: (params: GridRenderCellParams<ReactionToCustomer>) => {
                return (
                    <Typography>
                        {reactionTypes.find(rt => rt.reaction_type_id === params.row.reaction_type_id)?.description}
                    </Typography>
                );
            }
        },
        { field: "name", headerName: t("ReactionsList.name_Label"), type: "string", flex: 2, width: 100, display: 'flex', }, 
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<ReactionToCustomer>) => {
                return (
                    <ActionButtons actions={[
                        {type: 'edit', action: () => handleOpenModal(params.row), },
                        {type: 'delete', action: () => handleDelete(params.row.id), },
                    ]} />
                );
            },
        },        
    ];

    const handleUpdate = async (data: ReactionToCustomer) => {
        if (!!selectedReactionToCustomer) {
            await api.putReactionToCustomer(data);
            setData((prev) => prev.map(rtc => rtc.reaction_type_id === data.reaction_type_id ? data : rtc));
        } else {
            const newData = await api.postReactionToCustomer(data);
            setData((prev) => [...prev, newData]);
        }
        handleCloseModal();
    };

    const handleDelete = async (id: number) => {
        const result = await showConfirm(
            t('ReactionList.Are_you_sure_you_want_to_delete_the_reaction') + ' ' + id + '?', 
            t('common.delete_confirmation'));

        if (!result) return;

        await api.deleteReactionToCustomer(id);

        const updatedRTC = data.filter(rtc => id !== rtc.id);
        setData(updatedRTC);
        console.log(`ReactionToCustomer with ReactionToCustomerID ${id} deleted.`);
    };

    const handleOpenModal = (rtc: ReactionToCustomer | null) => {
        setSelectedReactionToCustomer(rtc);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    // const handleTest = async () => {
    //     showInfo(t('ReactionForm.Test_Reaction_Description'), t('ReactionForm.Test_Reaction'));
    // };

// --- Рендер карточки для мобильных устройств ---
    const renderMobileCard = (rtc: ReactionToCustomer) => (
        <Card key={rtc.reaction_type_id} sx={{ mb: 2, boxShadow: 2 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {/* <Typography variant="h6" color="primary">
                        {rtc.name}
                    </Typography> */}
                    <Box>
                        <IconButton onClick={() => handleOpenModal(rtc)} size="small"><Edit /></IconButton>
                        <IconButton onClick={() => handleDelete(rtc.id)} size="small" color="error"><Delete /></IconButton>
                    </Box>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{t('ReactionsList.group')}:</strong> {reactionTypes.find(rt => rt.reaction_type_id === rtc.reaction_type_id)?.name}</Typography>
                </Stack>
            </CardContent>
        </Card>
    );
    
    return (
  
        <Box sx={{ height: 400, width: '100%', p: isMobile ? 1 : 0 }}>
            
            {DialogComponent} {/* for 'confirm' dialog*/}
            
            <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2} 
                sx={{ mb: 2 }} 
                alignItems="center"
            >
                <Button 
                    fullWidth={isMobile}
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal(null)}
                >
                    {t('ReactionsList.Add_New_Reaction')}
                </Button>
            </Stack>

            {/* Основной контент: Сетка или Карточки */}
            {isMobile ? (
                <Box sx={{ pb: 2 }}>
                    {data.map(plate => renderMobileCard(plate))}
                </Box>
            ) : (
                <LocalizedGrid
                    lang={i18n.language as any}
                    gridId='ReactionsList'
                    rows={data}
                    columns={columns}
                    getRowId={(row) => row.id}
                    hideToolbar={true}
                    initialState={{
                        columns: {columnVisibilityModel: {id: false}}
                    }}
                />
            )}

            <Typography color='error'>{error?.message}</Typography>

            <FormModal open={isModalOpen} onClose={handleCloseModal}>
                <ReactionForm
                    reactionToCustomer={selectedReactionToCustomer}
                    onSave={handleUpdate}
                    onCancel={handleCloseModal}
                    // onTest={handleTest}
                    reactionTypes={reactionTypes}
                />
                {/* <Typography>Modal content goes here.</Typography> */}
            </FormModal>
        </Box>
  
    );
};